import { Controller } from '@hotwired/stimulus'
import * as Toastr from 'toastr'

// Validates form fields and checks Postmark sender verification status.
// Automatically validates any .form-group.required elements by finding inputs,
// selects, and trix editors within them. Verifies the sender email is approved
// in Postmark before allowing form submission.
export default class extends Controller {
  static values = {
    checkSenderVerification: Boolean,
    senderEmail: String,
    emailNotVerifiedMessage: String,
    verificationLink: String,
    errorMessage: String,
    successMessage: String,
    errorNotification: String
  }

  // Initializes the controller and checks sender verification status on page load.
  // If already verified, sets flag to skip verification check on form submit.
  connect() {
    this.senderVerified = false
    this.checkInitialVerification()
  }

  // Checks Postmark sender verification on page load. If the sender is already
  // verified, sets senderVerified flag so form submit skips the verification check.
  async checkInitialVerification() {
    if (!this.checkSenderVerificationValue) return

    const result = await this.checkVerification()
    if (result.verified) {
      this.senderVerified = true
    }
  }

  // Toggles sender verification based on checkbox state. Call this from a
  // checkbox change event to enable/disable sender verification dynamically.
  toggleSenderVerification(event) {
    this.checkSenderVerificationValue = event.target.checked
  }

  // Main form submit handler. Validates required fields, then checks sender
  // verification if enabled and not already verified. If verification passes,
  // re-submits the form; otherwise shows verification error.
  async validate(event) {
    if (!this.validateRequiredFields(event)) {
      return false
    }

    if (this.checkSenderVerificationValue && !this.senderVerified) {
      event.preventDefault()
      event.stopPropagation()

      const verificationResult = await this.checkVerification()

      if (verificationResult.verified) {
        this.senderVerified = true
        this.element.querySelector('form').requestSubmit()
      } else {
        this.showVerificationError(verificationResult)
      }

      return false
    }

    return true
  }

  // Finds all .form-group.required elements and validates their inputs.
  // Validates all fields at once, highlights invalid ones, and focuses the first.
  // Returns false if any validation fails, true if all fields are valid.
  validateRequiredFields(event) {
    const requiredGroups = this.element.querySelectorAll('.form-group.required')
    const invalidFields = []

    this.clearValidationErrors()

    for (const group of requiredGroups) {
      const label = group.querySelector('label')
      const fieldName = label ? label.textContent.trim() : 'This field'

      const trixEditor = group.querySelector('trix-editor')
      if (trixEditor) {
        if (!this.isValidTrixElement(trixEditor)) {
          invalidFields.push({ element: trixEditor, fieldName, group })
        }
        continue
      }

      const input = group.querySelector('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea')
      if (input) {
        if (!this.isValidInputElement(input)) {
          invalidFields.push({ element: input, fieldName, group })
        }
        continue
      }

      const select = group.querySelector('select')
      if (select) {
        if (!this.isValidSelectElement(select)) {
          invalidFields.push({ element: select, fieldName, group })
        }
        continue
      }
    }

    if (invalidFields.length > 0) {
      event.preventDefault()
      event.stopPropagation()

      invalidFields.forEach(({ element, fieldName, group }) => {
        this.showFieldError(element, fieldName, group)
      })

      invalidFields[0].element.focus()
      return false
    }

    return true
  }

  // Clears all validation error states from the form.
  clearValidationErrors() {
    this.element.querySelectorAll('.is-invalid').forEach(el => {
      el.classList.remove('is-invalid')
    })
    this.element.querySelectorAll('.invalid-feedback').forEach(el => {
      el.remove()
    })
  }

  // Marks a field as invalid and shows an error message at the end of its form group.
  showFieldError(element, fieldName, group) {
    element.classList.add('is-invalid')

    const existingFeedback = group.querySelector('.invalid-feedback')
    if (existingFeedback) {
      existingFeedback.remove()
    }

    const feedback = document.createElement('div')
    feedback.className = 'invalid-feedback'
    feedback.style.display = 'block'
    feedback.textContent = `${fieldName} is required.`

    group.appendChild(feedback)
  }

  // Checks if a text input or textarea element has a value.
  isValidInputElement(input) {
    return input.value.trim().length > 0
  }

  // Checks if a select element has a value selected.
  isValidSelectElement(select) {
    return select.value && select.value.length > 0
  }

  // Checks if a Trix rich text editor has content.
  isValidTrixElement(trixEditor) {
    if (trixEditor && trixEditor.editor) {
      const content = trixEditor.editor.getDocument().toString().trim()
      return content.length > 0
    }
    return true
  }

  // Calls the Postmark verification API to check if the sender email is verified.
  // Returns an object with verified status, verification state, email, and sender ID.
  async checkVerification() {
    try {
      const response = await fetch(`/postmark_invitations/verify?email=${encodeURIComponent(this.senderEmailValue)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (response.ok) {
        return {
          verified: data.verified,
          status: data.status,
          email: data.email,
          senderId: data.sender_id
        }
      } else {
        return { verified: false, status: 'error', email: this.senderEmailValue }
      }
    } catch (error) {
      console.error('Verification check failed:', error)
      return { verified: false, status: 'error', email: this.senderEmailValue }
    }
  }

  // Displays a persistent error toast explaining why the email cannot be sent
  // and provides a clickable link to initiate or resend verification.
  showVerificationError(result) {
    const message = this.buildMessage(result)
    const toast = Toastr.error(message, 'Cannot Send Email', {
      timeOut: 0,
      extendedTimeOut: 0,
      closeButton: true,
      tapToDismiss: false
    })

    this.errorToast = toast
    this.attachClickHandler(toast, result)
  }

  // Builds the appropriate error message based on verification status.
  // Shows actionable link for email_not_found or email_not_confirmed states.
  buildMessage(result) {
    if (result.status === 'email_not_found' || result.status === 'email_not_confirmed') {
      return `${this.emailNotVerifiedMessageValue} <a href="#" class="verification-action" style="color: inherit; font-weight: bold; text-decoration: underline;">${this.verificationLinkValue}</a>`
    } else {
      return this.errorMessageValue
    }
  }

  // Attaches click handler to the verification link in the error toast.
  // Triggers sendInvite for new emails or resendConfirmation for pending ones.
  attachClickHandler(toast, result) {
    if (!toast) return

    const link = toast.find('.verification-action')
    if (link.length === 0) return

    link.on('click', async (event) => {
      event.preventDefault()
      event.stopPropagation()

      if (result.status === 'email_not_found') {
        await this.sendInvite()
      } else if (result.status === 'email_not_confirmed') {
        await this.resendConfirmation(result.senderId)
      }
    })
  }

  // Sends a new Postmark sender signature invitation for the email address.
  // Used when the email has never been registered with Postmark.
  async sendInvite() {
    try {
      const response = await fetch('/postmark_invitations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: `email=${encodeURIComponent(this.senderEmailValue)}`
      })

      const data = await response.json()

      if (response.ok) {
        this.dismissErrorToast()
        Toastr.success(this.successMessageValue)
      } else {
        Toastr.error(data.error || this.errorNotificationValue)
      }
    } catch (error) {
      console.error('Error', error)
      Toastr.error(this.errorNotificationValue)
    }
  }

  // Resends the Postmark confirmation email for a pending sender signature.
  // Used when the email exists in Postmark but hasn't been confirmed yet.
  async resendConfirmation(senderId) {
    try {
      const response = await fetch(`/postmark_invitations/${senderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.dismissErrorToast()
        Toastr.success(this.successMessageValue)
      } else {
        Toastr.error(data.error || this.errorNotificationValue)
      }
    } catch (error) {
      console.error('Error', error)
      Toastr.error(this.errorNotificationValue)
    }
  }

  // Clears the persistent error toast after a successful verification action.
  dismissErrorToast() {
    if (this.errorToast) {
      Toastr.clear(this.errorToast)
      this.errorToast = null
    }
  }
}
